import React, { useMemo, useState, useEffect } from 'react'
import { useField } from 'formik'
import { useRelationField } from '../../../../hooks/components/useRelationField'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

const PAGE_SIZE = 10

const highlightMatch = (text: string, query: string) => {
  if (!query) return text
  const safe = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${safe})`, 'gi')
  const parts = text.split(regex)
  return (
    <>
      {parts.map((part, i) =>
        regex.test(part) ? (
          <span key={i} style={{ fontWeight: 700 }}>
            {part}
          </span>
        ) : (
          <span key={i} style={{ fontWeight: 300 }}>
            {part}
          </span>
        )
      )}
    </>
  )
}

type Item = {
  id: number
  name: string
  description?: string
}

type Props = {
  name: string
  placeholder?: string
  minChars?: number
}

export default function RelationAutocompleteField({
  name,
  minChars = 0,
}: Props) {
  const [field, , helpers] = useField<{ id: number; name: string } | undefined>(name)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(field.value?.name ?? '')
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // debounce
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 200)
    return () => clearTimeout(id)
  }, [query])

  const enableSearch = debounced.length >= minChars && debounced.length > 0

  const { collection, loading, error } = useRelationField()
  const items = useMemo<Item[]>(() => (Array.isArray(collection) ? collection : []), [collection])
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [debounced])

  const filtered = useMemo(() => {
    if (!enableSearch) return items
    const q = debounced.toLowerCase()
    return items.filter((c) => c.name.toLowerCase().includes(q))
  }, [items, enableSearch, debounced])

  const visible = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  )

  const hasMore = filtered.length > visibleCount

  const handleScroll = useNearBottom(() => {
    if (hasMore) setVisibleCount((c) => c + PAGE_SIZE)
  })

  useEffect(() => {
    if (field.value?.name) setQuery(field.value.name)
  }, [field.value?.name])

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        <input
          className='nb-language-input'
          placeholder='Select relation'
          value={query}
          onChange={(e) => {
            const v = e.target.value
            setQuery(v)
            if (field.value) helpers.setValue(undefined)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => {
            setOpen(false)
            setQuery(field.value?.name ?? '')
          }, 120)}
          autoComplete='off'
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {loading && enableSearch && (
            <div className='nb-language-item' style={{ color: '#808080', cursor: 'default' }}>
              Searching…
            </div>
          )}

          {!enableSearch && minChars > 0 && query.trim().length < minChars && (
            <div className='nb-language-item' style={{ color: '#808080', cursor: 'default' }}>
              Write at least {minChars} characters…
            </div>
          )}

          {error && enableSearch && (
            <div className='nb-language-item' style={{ color: '#808080', cursor: 'default' }}>
              Error loading
            </div>
          )}

          {!loading && enableSearch && !error && visible.length === 0 && (
            <div className='nb-language-item' style={{ color: '#808080', cursor: 'default' }}>
              No results
            </div>
          )}

          {!loading && !error && visible.length > 0 && (
            <>
              {visible.map((item) => (
                <div
                  key={item.id}
                  className='nb-language-item'
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    helpers.setValue({ id: item.id, name: item.name }) 
                    setQuery(item.name)
                    setOpen(false)
                  }}
                >
                  {highlightMatch(item.name, enableSearch ? debounced : '')}
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}
